<template>
  <UserLayout :article-step="1">
    <ArticleChatShell
      v-if="showTopicChat"
      :mode="chatMode"
      :value="chatPrompt"
      :keyword="keyword"
      :asking="chatAsking"
      :busy="loading"
      :resume-offer="setupPhase === 'resume'"
      :location-code="locationCode"
      :language-code="languageCode"
      :geo-template="geoTemplate"
      :tone="tone"
      :audience="audience"
      :depth="depth"
      :secondary-keywords="secondaryKeywords"
      :secondary-draft="secondaryDraft"
      :suggestions="suggestions"
      :suggestions-loading="suggestionsLoading"
      :history-rows="compactRows"
      :entered-rows="enteredRows"
      :flow="isAnalyze ? 'analyze' : 'create'"
      :url="articleUrl"
      :loaded-title="loadedTitle"
      :loaded-headings="loadedHeadings"
      :reading="readingPage"
      :improve-goal="improveGoal"
      :section-uids="sectionUids"
      @input="onChatPrompt"
      @keyword="onKeyword"
      @template="onTemplate"
      @tone="onTone"
      @audience="onAudience"
      @depth="onDepth"
      @secondary-draft="onSecondaryDraft"
      @url="onArticleUrl"
      @secondary-add="addSecondary"
      @secondary-remove="removeSecondary"
      @continue="confirmCurrent"
      @choose="onChatChoice"
      @cancel="leaveSetup"
      @resume="acceptDraft"
      @restart="restartDraft"
      @change="editStep"
      @locale="onLocale"
      @goal="onGoal"
      @sections="onSections"
    />
    <template v-else>
      <div
        v-if="showTopicHandoff"
        class="chat-handoff"
        role="log"
        aria-live="polite"
      >
        <p class="chat-handoff__row">
          <span>✓ {{ topicStepNumber }} {{ $t('articleChat.historyTopic') }} · {{ chatPromptPreview }}</span>
          <span data-test="article-chat-handoff-locale">{{ handoffLocale }}</span>
          <button
            type="button"
            class="chat-handoff__change"
            data-test="article-chat-change"
            @click="openSummary"
          >
            {{ $t('articleChat.change') }}
          </button>
        </p>
        <p class="chat-handoff__lead">
          {{ $t('articleChat.formLead') }}
        </p>
      </div>
      <KeywordForm
        :loading="loading"
        :is-analyze="isAnalyze"
        :preset-ai-prompt="showTopicHandoff ? chatPrompt : ''"
        :preset-location-code="showTopicHandoff ? locationCode : ''"
        :preset-language-code="showTopicHandoff ? languageCode : ''"
        :hide-brief="showTopicHandoff"
        @input="onKeywordInput"
      />
    </template>
    <v-dialog
      v-model="usageDialog"
      max-width="600"
      persistent
    >
      <v-card class="pa-10">
        <v-card-title>
          {{ dialogText }}
        </v-card-title>
        <v-card-actions v-if="dialogAction">
          <v-btn v-if="hasReachedStopLimit" depressed @click="$router.push('/')">
            {{ $t('cancel') }}
          </v-btn>
          <v-spacer />
          <v-btn
            :color="dialogActionColor"
            @click="dialogAction"
          >
            {{ dialogActionText }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </UserLayout>
</template>

<script lang="ts">
    import Vue from 'vue';
    import {Article, ArticleMetadata} from "@/model/Article";
    import {Firestore} from "@/plugins/Firestore";
    import {Functions} from "@/plugins/Functions";
    import UserLayout from "@/layouts/UserLayout.vue";
    import KeywordForm from "@/components/keywords/KeywordForm.vue";
    import ArticleChatShell from "@/components/articleChat/ArticleChatShell.vue";
    import UserPlanMixin from "@/mixins/UserPlanMixin";
    import {TranslateResult} from "vue-i18n";
    import { Toast } from '@/model/Toast';
    import {
        defaultWritingBrief,
        normalizeGeoTemplate,
        type ArticleAudienceId,
        type ArticleDepthId,
        type ArticleToneId,
        type GeoTemplateId,
    } from '@/config/WritingBrief';
    import {articleAccessFromStore, runWhenPlanLoaded} from '@/service/articleLimitGate';
    import {
        ARTICLE_CHAT_DEFAULT_LANGUAGE,
        ARTICLE_CHAT_DEFAULT_LOCATION,
        ARTICLE_CHAT_SUGGESTION_DELAY_MS,
        ARTICLE_CHAT_TOPIC_STEP,
        type ArticleChatDraftPhase,
        type ArticleChatReturnTo,
        type ArticleChatStepId,
        type ArticleChatSuggestion,
        type CreateSetupPhase,
        type HistoryRow,
        articleChatDraftKey,
        articleUrlReady,
        canContinueTopic,
        chatFlow,
        chatStepNumber,
        clearArticleChatDraft,
        commitSecondaryKeyword,
        compactHistory,
        DEFAULT_IMPROVE_GOAL,
        editFrom,
        enteredSteps,
        firstQueryString,
        goalCanContinue,
        goalSectionsFromHeadings,
        initialCreateSetup,
        keywordReady,
        loadedPageFromScrape,
        localeChoiceLabel,
        normalizeImproveGoal,
        normalizeLocale,
        normalizeSectionUids,
        phaseAfterResume,
        prefersReducedMotion,
        readArticleChatDraft,
        suggestionRequest,
        topicPreview,
        urlContinuePlan,
        continueSetupPhaseAfterShell,
        writeArticleChatDraft,
        type ImproveGoalId,
        type LoadedHeading,
    } from '@/service/articleChatSetup';
    import {readChatWritePath, type LucyWritePath} from '@/utils/articleWriteCta';
    import {markAnalyzeScrapeFailPending} from '@/service/analyzeScrapeOutcome';

    export default Vue.extend({
        name: "CreateArticleView",
        components: {ArticleChatShell, KeywordForm, UserLayout},
        mixins: [UserPlanMixin],
        props: {
            isAnalyze: {
                type: Boolean,
                default: false
            }
        },
        data: () => ({
            article: new Article,
            loading: false,
            usageDialog: false,
            dialogText: '' as TranslateResult,
            dialogAction: null as null | (() => any),
            dialogActionText: '' as TranslateResult,
            dialogActionColor: 'primary',
            chatPrompt: '',
            articleUrl: '',
            loadedTitle: '',
            loadedHeadings: [] as LoadedHeading[],
            loadedUrl: '',
            loadedReadToken: 0,
            readingPage: false,
            improveGoal: DEFAULT_IMPROVE_GOAL as ImproveGoalId,
            sectionUids: [] as string[],
            setupPhase: 'topic' as CreateSetupPhase,
            draftPhase: 'topic' as ArticleChatDraftPhase,
            chatAsking: false,
            askingTimer: 0,
            topicStepNumber: ARTICLE_CHAT_TOPIC_STEP,
            locationCode: ARTICLE_CHAT_DEFAULT_LOCATION,
            languageCode: ARTICLE_CHAT_DEFAULT_LANGUAGE,
            keyword: '',
            geoTemplate: defaultWritingBrief.geoTemplate as GeoTemplateId,
            geoTemplateUserPicked: false,
            tone: defaultWritingBrief.tone as ArticleToneId,
            audience: defaultWritingBrief.audience as ArticleAudienceId,
            depth: defaultWritingBrief.depth as ArticleDepthId,
            secondaryKeywords: [] as string[],
            secondaryDraft: '',
            suggestions: [] as ArticleChatSuggestion[],
            suggestionsLoading: false,
            suggestionTimer: 0,
            suggestionToken: 0,
            returnTo: null as ArticleChatReturnTo,
            pendingReturnTo: null as ArticleChatReturnTo,
            chatWritePath: null as LucyWritePath,
            urlScrapeExtractionFailed: false,
        }),
        computed: {
            showTopicChat: function (): boolean {
                if (this.isAnalyze) {
                    return true;
                }
                return this.setupPhase !== 'form';
            },
            activeFlow: function () {
                return chatFlow(this.isAnalyze);
            },
            showTopicHandoff: function (): boolean {
                return !this.isAnalyze && this.setupPhase === 'form';
            },
            chatMode: function (): string {
                if (
                    this.setupPhase === 'url'
                    || this.setupPhase === 'loaded'
                    || this.setupPhase === 'locale'
                    || this.setupPhase === 'keyword'
                    || this.setupPhase === 'template'
                    || this.setupPhase === 'style'
                    || this.setupPhase === 'goal'
                    || this.setupPhase === 'summary'
                ) {
                    return this.setupPhase;
                }
                return 'topic';
            },
            chatPromptPreview: function (): string {
                return topicPreview(this.chatPrompt);
            },
            localeNames: function (): {country: string; language: string} {
                const locale = this.$i18n && this.$i18n.locale ? String(this.$i18n.locale) : 'cs';
                return localeChoiceLabel(this.locationCode, this.languageCode, locale);
            },
            handoffLocale: function (): string {
                return String(this.$t('articleChat.localeRow', [this.localeNames.country, this.localeNames.language]));
            },
            urlRow: function (): HistoryRow {
                return {
                    id: 'url',
                    step: chatStepNumber('url', this.activeFlow),
                    title: String(this.$t('articleChat.historyUrl')),
                    value: topicPreview(this.articleUrl),
                };
            },
            loadedPreview: function (): string {
                const title = this.loadedTitle.trim();
                if (title) {
                    return topicPreview(title);
                }
                const first = this.loadedHeadings[0];
                return first ? topicPreview(first.text) : '';
            },
            loadedRow: function (): HistoryRow {
                return {
                    id: 'loaded',
                    step: chatStepNumber('loaded', this.activeFlow),
                    title: String(this.$t('articleChat.historyLoaded')),
                    value: this.loadedPreview,
                };
            },
            topicRow: function (): HistoryRow {
                return {
                    id: 'topic',
                    step: chatStepNumber('topic', this.activeFlow),
                    title: String(this.$t('articleChat.historyTopic')),
                    value: this.chatPromptPreview,
                };
            },
            localeRow: function (): HistoryRow {
                return {
                    id: 'locale',
                    step: chatStepNumber('locale', this.activeFlow),
                    title: String(this.$t('articleChat.localeLabel')),
                    value: `${this.localeNames.country} · ${this.localeNames.language}`,
                };
            },
            keywordRow: function (): HistoryRow {
                return {
                    id: 'keyword',
                    step: chatStepNumber('keyword', this.activeFlow),
                    title: String(this.$t('articleChat.historyKeyword')),
                    value: topicPreview(this.keyword),
                };
            },
            templateRow: function (): HistoryRow {
                return {
                    id: 'template',
                    step: chatStepNumber('template', this.activeFlow),
                    title: String(this.$t('articleChat.historyTemplate')),
                    value: String(this.$t('createBrief.templates.' + normalizeGeoTemplate(this.geoTemplate) + '.title')),
                };
            },
            styleRow: function (): HistoryRow {
                const tone = String(this.$t('createBrief.tones.' + this.tone));
                const audience = String(this.$t('createBrief.audiences.' + this.audience));
                const depth = String(this.$t('createBrief.depths.' + this.depth));
                return {
                    id: 'style',
                    step: chatStepNumber('style', this.activeFlow),
                    title: String(this.$t('articleChat.historyStyle')),
                    value: `${tone} · ${audience} · ${depth}`,
                };
            },
            goalPreview: function (): string {
                const goal = normalizeImproveGoal(this.improveGoal);
                const title = String(this.$t('articleChat.goals.' + goal + '.title'));
                if (goal !== 'rewrite') {
                    return title;
                }
                const names = goalSectionsFromHeadings(this.loadedHeadings)
                    .filter((section) => this.sectionUids.indexOf(section.id) !== -1)
                    .map((section) => section.text);
                if (!names.length) {
                    return title;
                }
                return topicPreview(`${title} · ${names.join(', ')}`);
            },
            goalRow: function (): HistoryRow {
                return {
                    id: 'goal',
                    step: chatStepNumber('goal', this.activeFlow),
                    title: String(this.$t('articleChat.historyGoal')),
                    value: this.goalPreview,
                };
            },
            historySource: function () {
                const shared = [this.topicRow, this.localeRow, this.keywordRow, this.templateRow, this.styleRow];
                const analyzeRows: HistoryRow[] = [this.urlRow];
                if (this.loadedHeadings.length) {
                    analyzeRows.push(this.loadedRow);
                }
                return {
                    phase: this.setupPhase,
                    returnTo: this.returnTo,
                    flow: this.activeFlow,
                    rows: this.isAnalyze ? analyzeRows.concat(shared, [this.goalRow]) : shared,
                };
            },
            compactRows: function (): HistoryRow[] {
                return compactHistory(this.historySource);
            },
            enteredRows: function (): HistoryRow[] {
                return enteredSteps(this.historySource);
            },
        },
        created: function () {
            this.bootChat();
        },
        beforeDestroy: function () {
            this.loadedReadToken += 1;
            this.clearAsking();
            this.clearSuggestionTimer();
        },
        methods: {
            chatStorage: function (): Storage | null {
                try {
                    return window.sessionStorage;
                } catch {
                    return null;
                }
            },
            bootChat: function () {
                const query = this.$route && this.$route.query ? this.$route.query : {};
                const queryPrompt = firstQueryString(query.aiPrompt);
                const queryKeyword = firstQueryString(query.keyword);
                const queryUrl = firstQueryString(query.url);
                const langloc = firstQueryString(query.langloc);
                const draft = readArticleChatDraft(this.chatStorage(), articleChatDraftKey(this.isAnalyze));
                const next = initialCreateSetup({
                    isAnalyze: this.isAnalyze,
                    queryPrompt,
                    queryUrl,
                    draft,
                });
                this.chatPrompt = next.aiPrompt;
                this.articleUrl = next.url;
                this.draftPhase = draft && draft.phase !== 'topic' ? draft.phase : (this.isAnalyze ? 'url' : 'topic');
                const fromDraft = next.phase === 'resume' && !!draft;
                this.locationCode = fromDraft && draft ? draft.locationCode : ARTICLE_CHAT_DEFAULT_LOCATION;
                this.languageCode = fromDraft && draft ? draft.languageCode : ARTICLE_CHAT_DEFAULT_LANGUAGE;
                this.keyword = fromDraft && draft ? draft.keyword : queryKeyword;
                this.geoTemplate = fromDraft && draft ? draft.geoTemplate : defaultWritingBrief.geoTemplate;
                this.geoTemplateUserPicked = fromDraft && draft ? draft.geoTemplateUserPicked : false;
                this.tone = fromDraft && draft ? draft.tone : defaultWritingBrief.tone;
                this.audience = fromDraft && draft ? draft.audience : defaultWritingBrief.audience;
                this.depth = fromDraft && draft ? draft.depth : defaultWritingBrief.depth;
                this.secondaryKeywords = fromDraft && draft ? draft.secondaryKeywords.slice() : [];
                this.secondaryDraft = '';
                this.loadedTitle = fromDraft && draft ? draft.loadedTitle : '';
                this.loadedHeadings = fromDraft && draft ? draft.loadedHeadings.slice() : [];
                this.loadedUrl = fromDraft && draft ? draft.loadedUrl : '';
                this.urlScrapeExtractionFailed = fromDraft && draft ? !!draft.urlScrapeExtractionFailed : false;
                this.improveGoal = fromDraft && draft ? draft.improveGoal : DEFAULT_IMPROVE_GOAL;
                this.sectionUids = fromDraft && draft ? draft.sectionUids.slice() : [];
                if (langloc && !fromDraft) {
                    const [language, location] = langloc.split('-');
                    const normalized = normalizeLocale(location || '', language || '');
                    this.locationCode = normalized.locationCode;
                    this.languageCode = normalized.languageCode;
                }
                this.pendingReturnTo = fromDraft && draft ? draft.returnTo : null;
                this.returnTo = next.phase === 'resume' ? null : this.pendingReturnTo;
                this.setupPhase = next.phase;
                if (next.phase !== 'form' && next.phase !== 'resume') {
                    this.startAsking();
                } else {
                    this.chatAsking = false;
                }
                if (this.keyword.trim()) {
                    this.queueSuggestions();
                }
                if (next.phase !== 'resume') {
                    this.persistDraft();
                }
            },
            startAsking: function () {
                this.clearAsking();
                if (prefersReducedMotion(window)) {
                    this.chatAsking = false;
                    return;
                }
                this.chatAsking = true;
                this.askingTimer = window.setTimeout(() => {
                    this.chatAsking = false;
                    this.askingTimer = 0;
                }, 500);
            },
            clearAsking: function () {
                if (this.askingTimer) {
                    window.clearTimeout(this.askingTimer);
                    this.askingTimer = 0;
                }
            },
            clearSuggestionTimer: function () {
                if (this.suggestionTimer) {
                    window.clearTimeout(this.suggestionTimer);
                    this.suggestionTimer = 0;
                }
            },
            queueSuggestions: function () {
                this.clearSuggestionTimer();
                const request = suggestionRequest(this.keyword, this.locationCode);
                if (!request) {
                    this.suggestions = [];
                    this.suggestionsLoading = false;
                    return;
                }
                this.suggestionsLoading = true;
                const token = this.suggestionToken + 1;
                this.suggestionToken = token;
                this.suggestionTimer = window.setTimeout(() => {
                    this.suggestionTimer = 0;
                    Firestore.getKeywordSuggestions(request.locationCode, request.keyword)
                        .then((list) => {
                            if (this.suggestionToken !== token) {
                                return;
                            }
                            this.suggestions = Array.isArray(list) ? list : [];
                        })
                        .catch(() => {
                            if (this.suggestionToken === token) {
                                this.suggestions = [];
                            }
                        })
                        .finally(() => {
                            if (this.suggestionToken === token) {
                                this.suggestionsLoading = false;
                            }
                        });
                }, ARTICLE_CHAT_SUGGESTION_DELAY_MS);
            },
            draftPhaseFromSetup: function (): ArticleChatDraftPhase {
                if (
                    this.setupPhase === 'url'
                    || this.setupPhase === 'loaded'
                    || this.setupPhase === 'locale'
                    || this.setupPhase === 'keyword'
                    || this.setupPhase === 'template'
                    || this.setupPhase === 'style'
                    || this.setupPhase === 'goal'
                    || this.setupPhase === 'summary'
                    || this.setupPhase === 'form'
                ) {
                    return this.setupPhase;
                }
                return 'topic';
            },
            persistDraft: function () {
                if (this.setupPhase === 'resume') {
                    return;
                }
                const key = articleChatDraftKey(this.isAnalyze);
                const hasPrompt = this.chatPrompt.trim().length > 0;
                const hasUrl = this.articleUrl.trim().length > 0;
                if (this.isAnalyze ? (!hasPrompt && !hasUrl) : !hasPrompt) {
                    clearArticleChatDraft(this.chatStorage(), key);
                    return;
                }
                writeArticleChatDraft(this.chatStorage(), {
                    aiPrompt: this.chatPrompt,
                    phase: this.draftPhaseFromSetup(),
                    locationCode: this.locationCode,
                    languageCode: this.languageCode,
                    returnTo: this.returnTo,
                    keyword: this.keyword,
                    geoTemplate: normalizeGeoTemplate(this.geoTemplate),
                    geoTemplateUserPicked: this.geoTemplateUserPicked,
                    tone: this.tone,
                    audience: this.audience,
                    depth: this.depth,
                    secondaryKeywords: this.secondaryKeywords.slice(),
                    url: this.isAnalyze ? this.articleUrl : '',
                    loadedTitle: this.isAnalyze ? this.loadedTitle : '',
                    loadedHeadings: this.isAnalyze ? this.loadedHeadings.slice() : [],
                    loadedUrl: this.isAnalyze ? this.loadedUrl : '',
                    urlScrapeExtractionFailed: this.isAnalyze && this.urlScrapeExtractionFailed,
                    improveGoal: normalizeImproveGoal(this.improveGoal),
                    sectionUids: normalizeSectionUids(this.sectionUids, goalSectionsFromHeadings(this.loadedHeadings)),
                }, key);
            },
            onArticleUrl: function (value: string) {
                this.articleUrl = value;
                if (value.trim() !== this.loadedUrl) {
                    this.urlScrapeExtractionFailed = false;
                }
                this.persistDraft();
            },
            onChatPrompt: function (value: string) {
                this.chatPrompt = value;
                this.persistDraft();
            },
            onKeyword: function (value: string) {
                this.keyword = value;
                this.persistDraft();
                this.queueSuggestions();
            },
            onTemplate: function (id: string) {
                this.geoTemplate = normalizeGeoTemplate(id);
                this.geoTemplateUserPicked = true;
                this.persistDraft();
            },
            onTone: function (id: ArticleToneId) {
                this.tone = id;
                this.persistDraft();
            },
            onAudience: function (id: ArticleAudienceId) {
                this.audience = id;
                this.persistDraft();
            },
            onDepth: function (id: ArticleDepthId) {
                this.depth = id;
                this.persistDraft();
            },
            onGoal: function (id: string) {
                this.improveGoal = normalizeImproveGoal(id);
                this.persistDraft();
            },
            onSections: function (ids: string[]) {
                this.sectionUids = normalizeSectionUids(ids, goalSectionsFromHeadings(this.loadedHeadings));
                this.persistDraft();
            },
            onSecondaryDraft: function (value: string) {
                this.secondaryDraft = value;
            },
            addSecondary: function () {
                const next = commitSecondaryKeyword(this.secondaryKeywords, this.secondaryDraft, this.keyword);
                this.secondaryKeywords = next.keywords;
                this.secondaryDraft = next.draft;
                this.persistDraft();
            },
            removeSecondary: function (index: number) {
                this.secondaryKeywords.splice(index, 1);
                this.persistDraft();
            },
            onLocale: function (choice: {locationCode: string; languageCode: string}) {
                this.locationCode = choice.locationCode;
                this.languageCode = choice.languageCode;
                this.persistDraft();
                if (this.keyword.trim()) {
                    this.queueSuggestions();
                }
            },
            confirmCurrent: function () {
                if (this.chatAsking || this.loading || this.readingPage) {
                    return;
                }
                const phase = this.setupPhase;
                if (phase === 'url') {
                    if (!articleUrlReady(this.articleUrl)) {
                        return;
                    }
                    this.continueFromUrl();
                    return;
                }
                if (phase === 'topic' && !canContinueTopic(this.chatPrompt)) {
                    return;
                }
                if (phase === 'keyword' && !keywordReady(this.keyword)) {
                    return;
                }
                if (phase === 'goal' && !goalCanContinue(this.improveGoal, this.sectionUids, this.loadedHeadings)) {
                    return;
                }
                if (phase !== 'loaded' && phase !== 'topic' && phase !== 'locale' && phase !== 'keyword' && phase !== 'template' && phase !== 'style' && phase !== 'goal') {
                    return;
                }
                if (phase === 'style') {
                    this.addSecondary();
                }
                const next = continueSetupPhaseAfterShell(phase, this.returnTo, this.activeFlow, {
                    skipGoalWithoutLoadedOutline: this.isAnalyze
                        && this.urlScrapeExtractionFailed
                        && !this.loadedHeadings.length,
                });
                this.returnTo = null;
                this.setupPhase = next;
                this.draftPhase = next === 'form' ? 'form' : next;
                this.persistDraft();
                this.startAsking();
            },
            onChatChoice: function (path: string) {
                const choice = readChatWritePath(path);
                if (!choice || this.chatAsking || this.loading) {
                    return;
                }
                this.chatWritePath = choice;
                this.submitFromChat();
            },
            submitFromChat: function () {
                if (this.chatAsking || this.loading) {
                    return;
                }
                if (this.isAnalyze && !articleUrlReady(this.articleUrl)) {
                    this.chatWritePath = null;
                    this.setupPhase = 'url';
                    this.returnTo = 'summary';
                    this.draftPhase = 'url';
                    this.persistDraft();
                    this.startAsking();
                    return;
                }
                if (!canContinueTopic(this.chatPrompt)) {
                    this.chatWritePath = null;
                    this.setupPhase = 'topic';
                    this.returnTo = 'summary';
                    this.draftPhase = 'topic';
                    this.persistDraft();
                    this.startAsking();
                    return;
                }
                if (!keywordReady(this.keyword)) {
                    this.chatWritePath = null;
                    this.setupPhase = 'keyword';
                    this.returnTo = 'summary';
                    this.draftPhase = 'keyword';
                    this.persistDraft();
                    this.startAsking();
                    return;
                }
                const pending = commitSecondaryKeyword(this.secondaryKeywords, this.secondaryDraft, this.keyword);
                this.secondaryKeywords = pending.keywords;
                this.secondaryDraft = pending.draft;
                this.returnTo = null;
                this.chatAsking = false;
                this.persistDraft();
                this.onKeywordInput({
                    keyword: this.keyword.trim(),
                    aiPrompt: this.chatPrompt,
                    url: this.isAnalyze ? this.articleUrl.trim() : '',
                    language_code: this.languageCode,
                    location_code: this.locationCode,
                    geoTemplate: normalizeGeoTemplate(this.geoTemplate),
                    geoTemplateUserPicked: this.geoTemplateUserPicked,
                    tone: this.tone,
                    audience: this.audience,
                    depth: this.depth,
                    secondaryKeywords: pending.keywords.slice(),
                } as ArticleMetadata);
            },
            editStep: function (id: ArticleChatStepId) {
                const next = editFrom(this.setupPhase, id);
                this.setupPhase = next.phase;
                this.returnTo = next.returnTo;
                this.draftPhase = next.phase;
                this.chatAsking = false;
                this.persistDraft();
            },
            openSummary: function () {
                this.setupPhase = 'summary';
                this.draftPhase = 'summary';
                this.returnTo = null;
                this.chatAsking = false;
                this.persistDraft();
            },
            clearLoadedSummary: function () {
                this.loadedTitle = '';
                this.loadedHeadings = [];
                this.loadedUrl = '';
            },
            continueFromUrl: function () {
                const url = this.articleUrl.trim();
                const same = url.length > 0 && url === this.loadedUrl && this.loadedHeadings.length > 0;
                const apply = (readOk: boolean, sameSummary: boolean) => {
                    const plan = urlContinuePlan({
                        returnTo: this.returnTo,
                        sameSummary,
                        readOk,
                    });
                    this.returnTo = plan.returnTo;
                    this.setupPhase = plan.phase;
                    this.draftPhase = plan.phase === 'form' ? 'form' : plan.phase;
                    this.persistDraft();
                    this.startAsking();
                };
                if (same) {
                    apply(true, true);
                    return;
                }
                this.sectionUids = [];
                const token = this.loadedReadToken + 1;
                this.loadedReadToken = token;
                this.readingPage = true;
                Functions.GetScrapped({site: url})
                    .then((response) => {
                        if (this.loadedReadToken !== token || this.articleUrl.trim() !== url) {
                            return;
                        }
                        const summary = loadedPageFromScrape(response);
                        if (!summary) {
                            this.clearLoadedSummary();
                            this.urlScrapeExtractionFailed = true;
                            apply(false, false);
                            return;
                        }
                        this.urlScrapeExtractionFailed = false;
                        this.loadedTitle = summary.title;
                        this.loadedHeadings = summary.headings.slice();
                        this.loadedUrl = url;
                        apply(true, false);
                    })
                    .catch(() => {
                        if (this.loadedReadToken !== token || this.articleUrl.trim() !== url) {
                            return;
                        }
                        this.clearLoadedSummary();
                        this.urlScrapeExtractionFailed = true;
                        apply(false, false);
                    })
                    .finally(() => {
                        if (this.loadedReadToken === token) {
                            this.readingPage = false;
                        }
                    });
            },
            acceptDraft: function () {
                let phase = phaseAfterResume(this.draftPhase, true);
                if (phase === 'loaded' && !this.loadedHeadings.length) {
                    phase = 'url';
                }
                this.setupPhase = phase;
                this.returnTo = this.pendingReturnTo;
                this.pendingReturnTo = null;
                if (phase !== 'form') {
                    this.startAsking();
                } else {
                    this.chatAsking = false;
                }
                this.persistDraft();
            },
            restartDraft: function () {
                clearArticleChatDraft(this.chatStorage(), articleChatDraftKey(this.isAnalyze));
                this.chatPrompt = '';
                this.articleUrl = '';
                this.clearLoadedSummary();
                this.improveGoal = DEFAULT_IMPROVE_GOAL;
                this.sectionUids = [];
                this.locationCode = ARTICLE_CHAT_DEFAULT_LOCATION;
                this.languageCode = ARTICLE_CHAT_DEFAULT_LANGUAGE;
                this.keyword = '';
                this.geoTemplate = defaultWritingBrief.geoTemplate;
                this.geoTemplateUserPicked = false;
                this.tone = defaultWritingBrief.tone;
                this.audience = defaultWritingBrief.audience;
                this.depth = defaultWritingBrief.depth;
                this.secondaryKeywords = [];
                this.secondaryDraft = '';
                this.suggestions = [];
                this.suggestionsLoading = false;
                this.clearSuggestionTimer();
                this.returnTo = null;
                this.pendingReturnTo = null;
                this.draftPhase = this.isAnalyze ? 'url' : 'topic';
                this.setupPhase = this.isAnalyze ? 'url' : 'topic';
                this.startAsking();
            },
            leaveSetup: function () {
                this.$router.push('/');
            },
            onKeywordInput: function (input: ArticleMetadata) {
                this.loading = true;
                this.$set(this.article, 'keyword', input.keyword);
                this.$set(this.article, 'aiPrompt', input.aiPrompt);
                this.$set(this.article, 'noAiPrompt', !input.aiPrompt);
                this.$set(this.article, 'url', input.url.trim());
                this.$set(this.article, 'isAnalyze', this.isAnalyze);
                if (this.isAnalyze) {
                    this.$set(this.article, 'enhanced', 'pending');
                    this.$set(this.article, 'improveGoal', normalizeImproveGoal(this.improveGoal));
                    this.$set(this.article, 'sectionUids', this.improveGoal === 'rewrite'
                        ? normalizeSectionUids(this.sectionUids, goalSectionsFromHeadings(this.loadedHeadings))
                        : []);
                }
                this.$set(this.article, 'language_code', input.language_code);
                this.$set(this.article, 'location_code', input.location_code);
                this.$set(this.article, 'geoTemplate', normalizeGeoTemplate(input.geoTemplate));
                this.$set(this.article, 'geoTemplateUserPicked', !!input.geoTemplateUserPicked);
                this.$set(this.article, 'tone', input.tone || 'professional');
                this.$set(this.article, 'audience', input.audience || 'general');
                this.$set(this.article, 'depth', input.depth || 'standard');
                this.$set(this.article, 'secondaryKeywords', Array.isArray(input.secondaryKeywords) ? input.secondaryKeywords : []);
                
                const intent = this.isAnalyze ? 'analyze' : 'generate';
                const decision = articleAccessFromStore(this.$store, intent);
                if (decision.action === 'wait') {
                    runWhenPlanLoaded(this, () => this.onKeywordInput(input));
                    return;
                }
                if (decision.action === 'paywall') {
                    if (decision.reason === 'free-exhausted') {
                        this.$store.dispatch('toasts/add', {
                            type: 'error',
                            title: this.$t('subscriptions.freeAnalyses.exhausted.title') as string,
                            content: this.$t('subscriptions.freeAnalyses.exhausted.content', [this.maxFreeAnalyses]) as string,
                            action: {
                                text: this.$t('subscriptions.freeAnalyses.exhausted.action') as string,
                                callback: () => this.$router.push('/account')
                            }
                        });
                    } else if (decision.reason === 'no-plan') {
                        this.$store.dispatch('toasts/add', {
                            type: 'error',
                            title: 'subscriptions.paywall.generateRequiresPlan.title',
                            content: 'subscriptions.paywall.generateRequiresPlan.content',
                            action: {
                                text: 'subscriptions.paywall.generateRequiresPlan.action',
                                callback: () => this.$router.push('/account')
                            }
                        });
                    } else {
                        this.$store.dispatch('toasts/add', {
                            type: 'error',
                            title: 'subscriptions.noInspirationsLeftThisPeriodUpgradeToContinue.title',
                            content: 'subscriptions.noInspirationsLeftThisPeriodUpgradeToContinue.content',
                            action: {
                                text: 'subscriptions.noInspirationsLeftThisPeriodUpgradeToContinue.action',
                                callback: () => this.$router.push('/account')
                            }
                        });
                    }
                    this.loading = false;
                    return;
                }
                
                // Check usage limits for usage-limited plans
                // TODO: keep in sync with functions/src/Subscription/Subscription.ts
                if (this.hasReachedStopLimit) {
                    this.usageDialog = true;
                    this.dialogText = this.$t('subscriptions.upgradeToBusiness.content', [(this as any).currentPlanLimits.stop]);
                    if (this.extraInspirations > 0) {
                        // Has extra credits - offer to use them
                        this.dialogAction = this.saveArticle; // BE will handle the subtraction
                        this.dialogActionText = this.$t('subscriptions.usageLimit.useExtraInspiration', [this.extraInspirations]);
                        this.dialogActionColor = 'accent';
                    } else {
                        // No extra credits - suggest Business upgrade
                        this.dialogAction = () => this.$router.push('/account');
                        this.dialogActionText = this.$t('subscriptions.upgradeToBusiness.action');
                        this.dialogActionColor = 'primary';
                    }
                    return;
                } else if (this.hasReachedWarningLimit) {
                    // Heavy plan user hit warning threshold (default: 40 articles)
                    this.usageDialog = true;
                    this.dialogText = this.$t('subscriptions.usageLimit.warning');
                    this.dialogAction = this.saveArticle;
                    this.dialogActionText = this.$t('continue');
                    this.dialogActionColor = 'accent';
                    return;
                } else {
                    // No usage limit issues - proceed with save
                    return this.saveArticle();
                }
            },
            saveArticle: function () {
                const path = readChatWritePath(this.chatWritePath);
                Firestore.saveUserArticle(this.article).then(articleId => {
                    if (this.isAnalyze && this.urlScrapeExtractionFailed) {
                        markAnalyzeScrapeFailPending(String(articleId));
                    }
                    clearArticleChatDraft(this.chatStorage());
                    this.$router.push(path
                        ? {path: '/article/' + articleId, query: {cesta: path}}
                        : {path: '/article/' + articleId});
                }).catch(error => {
                    console.error(error);
                    this.$store.dispatch('toasts/add', {
                        type: 'error',
                        title: 'unexpectedError',
                    } as Toast);
                }).finally(() => {
                    this.loading = false;
                });
            },
        }
    });
</script>

<style scoped lang="sass">
.chat-handoff
    max-width: 960px
    margin: 8px auto 0
    padding: 0 12px

.chat-handoff__row
    display: flex
    flex-wrap: wrap
    gap: 8px 12px
    align-items: center
    margin: 0

.chat-handoff__change
    min-height: 44px
    padding: 8px 4px
    border: 0
    background: transparent
    color: var(--sw-primary-fill)
    font: inherit
    cursor: pointer
    text-decoration: underline

    &:focus
        outline: 2px solid var(--sw-primary)
        outline-offset: 2px

.chat-handoff__lead
    margin: 0 0 4px
    color: var(--sw-text-2)
</style>
